/**
 * Campaign / UTM / ad-click parameter persistence.
 *
 * Ported from legacy `apps/web/src/utils/tracking/plugins/persist-campaign-plugin.ts`.
 * Reads campaign parameters from the URL on init and stores them in localStorage
 * with a 7-day TTL so they survive across page navigations.
 *
 * The checkout payload is built server-side, so `getCampaign()` is read in the
 * info form's submit handler and sent as a hidden `campaign` field — the same
 * way the Amplitude device id crosses that boundary (US-10014). Deliberately not
 * a cookie: this introduces no new storage, so there is nothing new to declare
 * or categorise in the consent tool, and the localStorage key below already
 * exists in production.
 */

const CAMPAIGN_TTL_MS = 60 * 60 * 24 * 7 * 1000; // 7 days
const CAMPAIGN_STORAGE_KEY = '__persist_campaign';

export type CampaignData = Record<string, string | undefined>;

type PersistedCampaign = {
  timestamp: number;
  ttl: number;
  campaign: CampaignData;
};

/**
 * UTM parameter names that are read from the URL and mapped
 * to camelCase keys (e.g. `utm_source` -> `campaignSource`).
 */
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

/**
 * Ad-click identifiers, stored under their own names rather than `utm_`-stripped.
 *
 * `gclid` is what makes a booking attributable to a specific Google Ads click;
 * `gbraid` / `wbraid` are its cookieless equivalents (iOS app and web-to-app
 * journeys), which Google sends instead of `gclid` when it cannot set cookies.
 * `dclid` is the Display & Video equivalent. Legacy captured `gclid`/`dclid`
 * incidentally — the `analytics` package's campaign parser matches
 * `/^(d|g)clid/` — and vnext's hand-rolled replacement did not, which is half of
 * why paid bookings became unattributable (US-11347).
 */
const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'dclid'] as const;

/**
 * Reads campaign parameters from the current URL and persists them to
 * localStorage with a 7-day TTL.
 */
export function initCampaign(): void {
  const raw = readCampaignParams(
    typeof window === 'undefined' ? '' : window.location.search
  );

  if (Object.keys(raw).length === 0) {
    return;
  }

  const campaign = extractCampaignData(raw);

  try {
    const item: PersistedCampaign = {
      timestamp: Date.now(),
      ttl: CAMPAIGN_TTL_MS,
      campaign,
    };

    localStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(item));
  } catch {
    // localStorage may be unavailable — nothing to fall back to, and a booking
    // must not care.
  }
}

/**
 * Extracts the campaign parameters we care about from a query string.
 *
 * Exported for tests: what this misses can never reach the order.
 */
export function readCampaignParams(search: string): Record<string, string> {
  const raw: Record<string, string> = {};

  try {
    const searchParams = new URLSearchParams(search);

    for (const param of UTM_PARAMS) {
      const value = searchParams.get(param);
      if (value) {
        // Strip the `utm_` prefix
        raw[param.slice(4)] = value;
      }
    }

    // Also capture any additional utm_* parameters not in the predefined list
    searchParams.forEach((value, key) => {
      if (key.startsWith('utm_') && value) {
        raw[key.slice(4)] = value;
      }
    });

    // Click ids keep their own names: `gclid` maps to `campaignGclid`, which is
    // the field the warehouse already stores as `campaign_gclid`.
    for (const param of CLICK_ID_PARAMS) {
      const value = searchParams.get(param);
      if (value) {
        raw[param] = value;
      }
    }
  } catch {
    return {};
  }

  return raw;
}

const U_REFERRER_STORAGE_KEY = 'understory:u_referrer';
const U_REFERRERS = [
  'booking-widget',
  'giftcard-widget',
  'upcoming-events-widget',
  'wordpress-plugin',
];

/**
 * Keeps the widget a visit came from (`?u_referrer=`) for the rest of the visit
 * and strips it from the address bar so it isn't copied into shared links.
 *
 * Runs in `entry.client.tsx` before hydration: stripping it later would either
 * re-run loaders via a router navigation or leave it in router state.
 */
export function captureUReferrer(): void {
  const url = new URL(window.location.href);
  const referrer = url.searchParams.get('u_referrer');
  if (referrer === null) return;

  if (U_REFERRERS.includes(referrer)) {
    try {
      sessionStorage.setItem(U_REFERRER_STORAGE_KEY, referrer);
    } catch {
      // Storage can be unavailable; the visit just goes unattributed.
    }
  }

  url.searchParams.delete('u_referrer');
  window.history.replaceState(window.history.state, '', url);
}

export function getUReferrer(): string | undefined {
  try {
    return sessionStorage.getItem(U_REFERRER_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Retrieves previously persisted campaign data, if still valid, plus the
 * visit's widget referrer — kept apart in sessionStorage so it never outlives
 * the visit the way the 7-day campaign does.
 */
export function getCampaign(): CampaignData | undefined {
  const uReferrer = getUReferrer();
  const campaign = getPersistedCampaign();
  return uReferrer ? { ...campaign, uReferrer } : campaign;
}

function getPersistedCampaign(): CampaignData | undefined {
  try {
    const raw = localStorage.getItem(CAMPAIGN_STORAGE_KEY);
    if (!raw) return undefined;

    const persisted: PersistedCampaign = JSON.parse(raw);
    const expired = Date.now() - persisted.timestamp > persisted.ttl;
    if (expired) {
      localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
      return undefined;
    }

    return persisted.campaign;
  } catch {
    return undefined;
  }
}

/**
 * Maps raw campaign key/value pairs to camelCase campaign keys.
 * For example: `{ source: "google" }` -> `{ campaignSource: "google" }`, and
 * `{ gclid: "Cj0K…" }` -> `{ campaignGclid: "Cj0K…" }`.
 *
 * The prefix is not cosmetic: the warehouse snake_cases these into
 * `campaign_source` / `campaign_gclid`, which are the fields the receipts and
 * payment.transactions protos already model, so this shape lands in existing
 * columns with no schema change.
 */
function extractCampaignData(raw: Record<string, string>): CampaignData {
  return Object.entries(raw).reduce<CampaignData>((acc, [key, value]) => {
    const formattedKey = `campaign${key.charAt(0).toUpperCase()}${key.slice(1)}`;
    acc[formattedKey] = value || undefined;
    return acc;
  }, {});
}
